import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { URI } from 'vscode/vscode/vs/base/common/uri';
import { MenuRegistry, MenuId } from 'vscode/vscode/vs/platform/actions/common/actions';
import { ITerminalService, ITerminalGroupService } from 'vscode/vscode/vs/workbench/contrib/terminal/browser/terminal.service';
import { ResourceContextKey } from 'vscode/vscode/vs/workbench/common/contextkeys';
import { IFileService } from 'vscode/vscode/vs/platform/files/common/files.service';
import { IListService } from 'vscode/vscode/vs/platform/list/browser/listService.service';
import { getMultiSelectedResources } from 'vscode/vscode/vs/workbench/contrib/files/browser/files';
import { IExplorerService } from 'vscode/vscode/vs/workbench/contrib/files/browser/files.service';
import { CommandsRegistry } from 'vscode/vscode/vs/platform/commands/common/commands';
import { Schemas } from 'vscode/vscode/vs/base/common/network';
import { distinct } from 'vscode/vscode/vs/base/common/arrays';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';
import { IRemoteAgentService } from 'vscode/vscode/vs/workbench/services/remote/common/remoteAgentService.service';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { Extensions } from 'vscode/vscode/vs/workbench/common/contributions';
import { Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { isWindows } from 'vscode/vscode/vs/base/common/platform';
import { dirname, basename } from 'vscode/vscode/vs/base/common/path';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { IExternalTerminalService } from '../../../../platform/externalTerminal/common/externalTerminal.service.js';
import { TerminalLocation } from 'vscode/vscode/vs/platform/terminal/common/terminal';

const _moduleId = "vs/workbench/contrib/externalTerminal/browser/externalTerminal.contribution";
const OPEN_IN_TERMINAL_COMMAND_ID = 'openInTerminal';
const OPEN_IN_INTEGRATED_TERMINAL_COMMAND_ID = 'openInIntegratedTerminal';
function registerOpenTerminalCommand(id, explorerKind) {
    CommandsRegistry.registerCommand({
        id: id,
        handler: async (accessor, resource) => {
            const configurationService = accessor.get(IConfigurationService);
            const editorService = accessor.get(IEditorService);
            const fileService = accessor.get(IFileService);
            const integratedTerminalService = accessor.get(ITerminalService);
            const remoteAgentService = accessor.get(IRemoteAgentService);
            const terminalGroupService = accessor.get(ITerminalGroupService);
            let externalTerminalService = undefined;
            try {
                externalTerminalService = accessor.get(IExternalTerminalService);
            }
            catch {
            }
            const resources = getMultiSelectedResources(resource, accessor.get(IListService), editorService, accessor.get(IExplorerService));
            return fileService.resolveAll(( (resources.map(r => ({ resource: r }))))).then(async (stats) => {
                const config = configurationService.getValue();
                const useIntegratedTerminal = remoteAgentService.getConnection() || explorerKind === 'integrated';
                const targets = distinct(stats.filter(data => data.success));
                if (useIntegratedTerminal) {
                    const opened = {};
                    const cwds = ( (targets.map(({ stat }) => {
                        const resource = stat.resource;
                        if (stat.isDirectory) {
                            return resource;
                        }
                        return (
                             (URI.from({
                                scheme: resource.scheme,
                                authority: resource.authority,
                                fragment: resource.fragment,
                                query: resource.query,
                                path: dirname(resource.path)
                            }))
                        );
                    })));
                    for (const cwd of cwds) {
                        if (opened[cwd.path]) {
                            return;
                        }
                        opened[cwd.path] = true;
                        const instance = await integratedTerminalService.createTerminal({ config: { cwd } });
                        if (instance && instance.target !== TerminalLocation.Editor && (resources.length === 1 || !resource || cwd.path === resource.path || cwd.path === dirname(resource.path))) {
                            integratedTerminalService.setActiveInstance(instance);
                            terminalGroupService.showPanel(true);
                        }
                    }
                }
                else if (externalTerminalService) {
                    distinct(( (targets.map(
                        ({ stat }) => stat.isDirectory ? stat.resource.fsPath : dirname(stat.resource.fsPath)
                    )))).forEach(cwd => {
                        externalTerminalService.openTerminal(config.terminal.external, cwd);
                    });
                }
            });
        }
    });
}
registerOpenTerminalCommand(OPEN_IN_TERMINAL_COMMAND_ID, 'external');
registerOpenTerminalCommand(OPEN_IN_INTEGRATED_TERMINAL_COMMAND_ID, 'integrated');
let ExternalTerminalContribution = class ExternalTerminalContribution extends Disposable {
    constructor(_configurationService) {
        super();
        this._configurationService = _configurationService;
        const shouldShowIntegratedOnLocal = ( (ContextKeyExpr.and(
             (ResourceContextKey.Scheme.isEqualTo(Schemas.file)),
             (ContextKeyExpr.or(
                 (ContextKeyExpr.equals('config.terminal.explorerKind', 'integrated')),
                 (ContextKeyExpr.equals('config.terminal.explorerKind', 'both'))
            ))
        )));
        const shouldShowExternalKindOnLocal = ( (ContextKeyExpr.and(
             (ResourceContextKey.Scheme.isEqualTo(Schemas.file)),
             (ContextKeyExpr.or(
                 (ContextKeyExpr.equals('config.terminal.explorerKind', 'external')),
                 (ContextKeyExpr.equals('config.terminal.explorerKind', 'both'))
            ))
        )));
        this._openInIntegratedTerminalMenuItem = {
            group: 'navigation',
            order: 30,
            command: {
                id: OPEN_IN_INTEGRATED_TERMINAL_COMMAND_ID,
                title: ( localizeWithPath(_moduleId, 0, "Open in Integrated Terminal"))
            },
            when: ( (ContextKeyExpr.or(
                shouldShowIntegratedOnLocal,
                 (ResourceContextKey.Scheme.isEqualTo(Schemas.vscodeRemote))
            )))
        };
        this._openInTerminalMenuItem = {
            group: 'navigation',
            order: 31,
            command: {
                id: OPEN_IN_TERMINAL_COMMAND_ID,
                title: ( localizeWithPath(_moduleId, 1, "Open in External Terminal"))
            },
            when: shouldShowExternalKindOnLocal
        };
        MenuRegistry.appendMenuItem(MenuId.ExplorerContext, this._openInTerminalMenuItem);
        MenuRegistry.appendMenuItem(MenuId.ExplorerContext, this._openInIntegratedTerminalMenuItem);
        this._configurationService.onDidChangeConfiguration(e => {
            if (e.affectsConfiguration('terminal.explorerKind') || e.affectsConfiguration('terminal.external')) {
                this._refreshOpenInTerminalMenuItemTitle();
            }
        });
        this._refreshOpenInTerminalMenuItemTitle();
    }
    isWindows() {
        const config = this._configurationService.getValue().terminal;
        if (isWindows && config.external?.windowsExec) {
            const file = basename(config.external.windowsExec);
            if (file === 'wt' || file === 'wt.exe') {
                return true;
            }
        }
        return false;
    }
    _refreshOpenInTerminalMenuItemTitle() {
        if (this.isWindows()) {
            this._openInTerminalMenuItem.command.title = ( localizeWithPath(_moduleId, 2, "Open in Windows Terminal"));
        }
    }
};
ExternalTerminalContribution = ( (__decorate([
    ( (__param(0, IConfigurationService)))
], ExternalTerminalContribution)));
( (Registry.as(Extensions.Workbench))).registerWorkbenchContribution(ExternalTerminalContribution, 3 );

export { ExternalTerminalContribution };
